import type { ValidationError } from '../types'

export function getErrorMessage(error: ValidationError): string {
  const ctx = error.context ?? {}
  switch (error.code) {
    case 'NO_ROOMS':
      return 'El proyecto no tiene recintos. Agrega al menos un recinto antes de generar.'
    case 'ROOM_NO_POINTS':
      return ctx.room_name
        ? `El recinto "${ctx.room_name}" no tiene puntos eléctricos. Agrega al menos una luz o enchufe.`
        : 'Uno o más recintos no tienen puntos eléctricos.'
    case 'NO_LIGHTING':
      return 'El proyecto no tiene puntos de iluminación. Agrega al menos una luz en algún recinto.'
    case 'NO_OUTLETS':
      return 'El proyecto no tiene enchufes. Agrega al menos un enchufe en algún recinto.'
    case 'DEMAND_EXCEEDS_MONOPHASIC': {
      const demandKW = ctx.demand_w != null ? (ctx.demand_w / 1000).toFixed(1) : null
      const limitKW = ctx.limit_w != null ? (ctx.limit_w / 1000).toFixed(1) : null
      return demandKW && limitKW
        ? `La demanda total (${demandKW} kW) supera el límite monofásico (${limitKW} kW). Contacta a un ingeniero eléctrico.`
        : 'La potencia total supera el límite para instalación monofásica. Contacta a un ingeniero eléctrico.'
    }
    case 'AMPACITY_INSUFFICIENT': {
      const circuit = ctx.circuit_name ?? 'un circuito'
      const cond = ctx.conductor_mm2 != null ? `${ctx.conductor_mm2}mm²` : null
      const recommended = ctx.recommended_mm2 != null ? `${ctx.recommended_mm2}mm²` : null
      return cond && recommended
        ? `El conductor de ${circuit} (${cond}) no soporta la corriente. Usa conductor de ${recommended} o mayor.`
        : `El conductor de ${circuit} no soporta la corriente. Revisa el calibre del cable.`
    }
    case 'VOLTAGE_DROP_EXCEEDED': {
      const circuit = ctx.circuit_name ?? 'un circuito'
      const drop = ctx.voltage_drop_pct != null ? `${ctx.voltage_drop_pct.toFixed(1)}%` : null
      const max = ctx.max_pct != null ? `${ctx.max_pct}%` : null
      return drop && max
        ? `Caída de tensión en ${circuit} (${drop}) supera el máximo permitido (${max}). Aumenta el calibre del conductor.`
        : `Caída de tensión excesiva en ${circuit}. Aumenta el calibre del conductor.`
    }
    case 'POWER_MISMATCH': {
      const circuits = ctx.circuits_total_w != null ? (ctx.circuits_total_w / 1000).toFixed(1) : null
      const panel = ctx.panel_power_w != null ? (ctx.panel_power_w / 1000).toFixed(1) : null
      return circuits && panel
        ? `La suma de circuitos (${circuits} kW) no coincide con la potencia del tablero (${panel} kW). Revisa el cálculo.`
        : 'La potencia de los circuitos no coincide con el tablero. Revisa el cálculo.'
    }
    case 'POINTS_MISMATCH': {
      const cp = ctx.circuits_points
      const rp = ctx.rooms_points
      return cp != null && rp != null
        ? `Los puntos en circuitos (${cp}) no coinciden con los puntos en recintos (${rp}). Revisa el proyecto.`
        : 'Los puntos eléctricos no cuadran entre recintos y circuitos. Revisa el proyecto.'
    }
    case 'MISSING_OWNER_NAME':
      return 'Falta el nombre del propietario. Completa los datos del proyecto.'
    case 'MISSING_INSTALLER_NAME':
      return 'Falta el nombre del instalador. Completa los datos del proyecto.'
    case 'MISSING_INSTALLER_LICENSE':
      return 'Falta la licencia del instalador. Completa los datos del proyecto.'
    case 'MISSING_INSTALLER_LICENSE_CLASS':
      return 'Falta la clase de licencia SEC del instalador. Completa los datos del proyecto.'
    case 'INSUFFICIENT_CIRCUITS': {
      const min = ctx.min_required
      const m2 = ctx.square_meters
      return min != null && m2 != null
        ? `La instalación de ${m2}m² requiere al menos ${min} circuitos. Agrega más puntos eléctricos.`
        : 'El proyecto no tiene suficientes puntos para generar los circuitos mínimos requeridos.'
    }
    case 'MAX_POINTS_EXCEEDED': {
      const circuit = ctx.circuit_name ?? 'un circuito'
      const count = ctx.points_count
      const max = ctx.max_points
      return count != null && max != null
        ? `El circuito ${circuit} tiene ${count} puntos, superando el máximo de ${max}. Redistribuye los puntos.`
        : `Un circuito supera el máximo de puntos permitido. Redistribuye los puntos eléctricos.`
    }
    case 'MISSING_EARTH_CONDUCTOR': {
      const circuit = ctx.circuit_name ?? 'un circuito'
      return `El circuito ${circuit} no tiene conductor de tierra. Verifica la configuración (RIC N°05).`
    }
    case 'DIFFERENTIAL_OVERLOAD': {
      const group = ctx.group != null ? `D${ctx.group}` : 'un grupo diferencial'
      const count = ctx.circuits_count
      const max = ctx.max_circuits
      return count != null && max != null
        ? `El diferencial ${group} protege ${count} circuitos, superando el máximo de ${max} (RIC N°10).`
        : `Un diferencial supera el máximo de circuitos permitido (RIC N°10 5.1.3.7).`
    }
    case 'SPECIAL_NOT_DEDICATED': {
      const circuit = ctx.circuit_name ?? 'un circuito especial'
      return `El circuito ${circuit} no es dedicado. Cada artefacto especial requiere su propio circuito.`
    }
    case 'IMAGE_TOO_LARGE':
      return 'La imagen es demasiado grande (máx. 5 MB). Se comprimirá automáticamente antes de subir.'
    case 'VISION_API_ERROR':
      return 'No pudimos conectar con el servicio de análisis. Intenta nuevamente en unos segundos.'
    case 'FLOOR_PLAN_DETECTION_FAILED':
      return 'No pudimos analizar el plano. Intenta con una imagen más clara o con mejor iluminación.'
    case 'FLOOR_PLAN_ANALYSIS_FAILED':
      return 'No detectamos recintos en el plano. Asegúrate de que la imagen muestre la planta completa y esté bien iluminada.'
    case 'FILE_TOO_LARGE':
      return 'El archivo supera el límite de 10 MB. Comprime la imagen o usa una de menor resolución.'
    case 'INVALID_FILE_TYPE':
      return 'Formato de archivo no soportado. Usa JPG, PNG o PDF.'
    case 'INVALID_IMAGE_FORMAT':
      return 'Formato de imagen no soportado. Usa JPG, PNG o PDF.'
    case 'IMAGE_UPLOAD_FAILED':
      return 'No pudimos subir la imagen. Verifica tu conexión e intenta nuevamente.'
    case 'SCALE_CONFIG_REQUIRED':
      return 'Falta la escala del plano. Calibra la escala en el tab Plano antes de calcular canalizaciones.'
    case 'PANEL_POSITION_REQUIRED':
      return 'Falta la posición del tablero. El punto TA debe estar colocado en el plano.'
    case 'CONDUIT_ROUTING_FAILED':
      return 'No pudimos calcular las canalizaciones. Revisa que todos los recintos tengan paredes conectadas.'
    case 'CONDUIT_NO_WALL_GRAPH':
      return 'No se pudo construir el grafo de paredes. Verifica que las paredes de los recintos estén conectadas.'
    case 'JUNCTION_BOX_OVERFLOW':
      return 'Demasiados circuitos en una caja de derivación. Redistribuye los puntos eléctricos.'
    case 'PROJECT_NO_SCALE':
      return 'Falta la escala del plano. Calibra la escala antes de calcular canalizaciones.'
    case 'SWITCHES_WITHOUT_LUMINAIRES': {
      const room = ctx.room_name
      return room
        ? `El recinto "${room}" tiene interruptores pero no tiene luminarias. Agrega una luz o elimina el interruptor.`
        : 'Un recinto tiene interruptores sin luminarias asignadas.'
    }
    case 'SWITCH_CONTROLS_MISSING_LUMINAIRE': {
      const room = ctx.room_name
      const missing = ctx.missing_luminaire_id
      return room
        ? `Un interruptor en "${room}" referencia una luminaria que no existe (${missing ?? 'ID desconocido'}). Revisa las conexiones del plano.`
        : 'Un interruptor referencia una luminaria que no existe en el recinto.'
    }
    case 'EMAIL_NOT_VERIFIED':
      return 'Verifica tu email para poder generar documentos. Revisa tu bandeja de entrada.'
    default:
      return `Error de validación (${error.code}).`
  }
}

export function getErrorMessageByCode(code: string): string {
  return getErrorMessage({ code, field: '', value: '', context: {} })
}

export function getErrorSeverity(code: string): 'blocking' | 'warning' {
  const blocking = [
    'NO_ROOMS',
    'ROOM_NO_POINTS',
    'NO_LIGHTING',
    'NO_OUTLETS',
    'DEMAND_EXCEEDS_MONOPHASIC',
    'MISSING_OWNER_NAME',
    'MISSING_INSTALLER_NAME',
    'MISSING_INSTALLER_LICENSE',
    'MISSING_INSTALLER_LICENSE_CLASS',
    'INSUFFICIENT_CIRCUITS',
    'MAX_POINTS_EXCEEDED',
    'MISSING_EARTH_CONDUCTOR',
    'SPECIAL_NOT_DEDICATED',
    'IMAGE_TOO_LARGE',
    'VISION_API_ERROR',
    'FLOOR_PLAN_DETECTION_FAILED',
    'INVALID_IMAGE_FORMAT',
    'IMAGE_UPLOAD_FAILED',
  ]
  return blocking.includes(code) ? 'blocking' : 'warning'
}
